import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { playSfx } from "@/lib/sound";
import { Ticket, Loader2 } from "lucide-react";
import { Countdown } from "@/components/Kit";

export default function Wheel() {
  const { patchUser } = useOutletContext();
  const canvasRef = useRef(null);
  const angleRef = useRef(0);
  const [wedges, setWedges] = useState([]);
  const [canSpin, setCanSpin] = useState(false);
  const [nextIn, setNextIn] = useState(0);
  const [spinning, setSpinning] = useState(false);

  const load = async () => {
    const { data } = await api.get("/wheel");
    setWedges(data.wedges); setCanSpin(data.can_spin); setNextIn(data.next_spin_in);
  };
  useEffect(() => { load(); }, []);

  const drawWheel = (rot) => {
    const c = canvasRef.current; if (!c || !wedges.length) return;
    const ctx = c.getContext("2d"); const W = c.width, cx = W / 2, r = W / 2 - 8;
    ctx.clearRect(0, 0, W, W);
    const step = (Math.PI * 2) / wedges.length;
    wedges.forEach((w, i) => {
      const a0 = rot + i * step, a1 = a0 + step;
      ctx.beginPath(); ctx.moveTo(cx, cx); ctx.arc(cx, cx, r, a0, a1); ctx.closePath();
      ctx.fillStyle = w.color; ctx.fill(); ctx.strokeStyle = "rgba(0,0,0,0.35)"; ctx.lineWidth = 2; ctx.stroke();
      ctx.save(); ctx.translate(cx, cx); ctx.rotate(a0 + step / 2); ctx.textAlign = "right";
      ctx.fillStyle = "#0a0a0a"; ctx.font = "bold 13px sans-serif"; ctx.fillText(w.label, r - 12, 5); ctx.restore();
    });
    ctx.beginPath(); ctx.arc(cx, cx, 26, 0, 7); ctx.fillStyle = "#111"; ctx.fill(); ctx.strokeStyle = "#FFB300"; ctx.lineWidth = 3; ctx.stroke();
    // pointer
    ctx.beginPath(); ctx.moveTo(W - 4, cx - 12); ctx.lineTo(W - 4, cx + 12); ctx.lineTo(W - 28, cx); ctx.closePath(); ctx.fillStyle = "#FFB300"; ctx.fill();
  };

  useEffect(() => { drawWheel(angleRef.current); }, [wedges]);

  const spin = async () => {
    if (spinning || !canSpin) return;
    setSpinning(true);
    try {
      const { data } = await api.post("/wheel/spin");
      const n = wedges.length, step = (Math.PI * 2) / n;
      // land pointer (at angle 0, right side) on the winning wedge center
      const target = (Math.PI * 2 * 6) + (-(data.index * step + step / 2));
      const start = angleRef.current, dur = 4200, t0 = performance.now();
      const ease = (x) => 1 - Math.pow(1 - x, 3);
      const frame = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        angleRef.current = start + (target - start) * ease(p);
        drawWheel(angleRef.current);
        if (p < 1) requestAnimationFrame(frame);
        else {
          playSfx("win");
          patchUser({ bits: data.wallet.bits, credits: data.wallet.bits, cosmium: data.wallet.cosmium });
          toast.success(`Wheel landed on ${data.wedge}!${data.granted ? ` +${data.granted}` : ""}`);
          setCanSpin(false); setSpinning(false); load();
        }
      };
      requestAnimationFrame(frame);
    } catch (e) { toast.error(e.response?.data?.detail || "The wheel is jammed."); setSpinning(false); load(); }
  };

  return (
    <div className="max-w-lg mx-auto text-center">
      <h1 data-testid="wheel-title" className="font-head font-black text-3xl sm:text-4xl neon-text mb-1">Wheel of Overclocking</h1>
      <p className="label-cap mb-6">Spin once every 24 hours for Bits &amp; Cosmium</p>
      <div className="panel-glow p-6 inline-block">
        <canvas ref={canvasRef} width={360} height={360} data-testid="wheel-canvas" className="max-w-full" />
      </div>
      <div className="mt-6">
        {canSpin ? (
          <button data-testid="wheel-spin-btn" onClick={spin} disabled={spinning} className="btn-primary text-base px-10 py-4">
            {spinning ? <><Loader2 className="w-5 h-5 animate-spin" /> Spinning…</> : <><Ticket className="w-5 h-5" /> Spin the Wheel</>}
          </button>
        ) : (
          <div data-testid="wheel-cooldown" className="panel px-6 py-4 inline-block">
            <div className="label-cap">Next spin in</div>
            <Countdown seconds={nextIn} className="font-head text-2xl font-black text-primary" onDone={load} />
          </div>
        )}
      </div>
    </div>
  );
}
